import { PublicKeyProvider } from './public-key-provider.js';
import pDefer from 'p-defer';
/**
 * This class provides a dev interface for injecting mocked XPUB keys (Extended Public Keys).
 * @see {@link https://github.com/bitcoin/bips/blob/master/bip-0032.mediawiki#user-content-Extended_keys|BIP32 - Extended keys}
 * @remarks
 * It can be either used via its exposed API, or through the IoC container by registering a "xpubs" definition.
 * @example
 * ```
 * container.register({
 *   id: 'xpubs',
 *   factory: () => {
 *     return {
 *       exodus_0: {
 *         bitcoin: {
 *           44: 'xpub6ByVzenfCtnyd48...'
 *         },
 *       },
 *     };
 *   }
 * })
 * ```
 */
export class MockablePublicKeyProvider extends PublicKeyProvider {
    onReady;
    #publicKeyStore;
    #assetsModule;
    #logger;
    #walletAccountsAtom;
    constructor({ config, assetsModule, publicKeyStore, walletAccountsAtom, logger, keychain, getBuildMetadata, }) {
        super({ keychain, logger, walletAccountsAtom, publicKeyStore, getBuildMetadata });
        const deferred = pDefer();
        this.onReady = deferred.promise;
        this.#assetsModule = assetsModule;
        this.#publicKeyStore = publicKeyStore;
        this.#walletAccountsAtom = walletAccountsAtom;
        this.#logger = logger;
        this.onReady = this.#ingestMocks(config.xpubs);
    }
    #processAssetSource = async (walletAccount, assetName, details) => {
        const walletAccountName = walletAccount.toString();
        const asset = this.#assetsModule.getAsset(assetName);
        const keys = Object.entries(details).flatMap(([purpose, xpub]) => {
            const keyIdentifier = asset.baseAsset.api?.getKeyIdentifier({
                accountIndex: walletAccount.index,
                compatibilityMode: walletAccount.compatibilityMode,
                purpose: Number(purpose),
            });
            this.#logger.debug('Mocking', assetName, 'xpub with derivation path', keyIdentifier?.derivationPath, 'for wallet account', walletAccountName);
            return keyIdentifier ? [{ keyIdentifier, xpub }] : [];
        });
        await this.#publicKeyStore.addMany({ walletAccount, keys });
    };
    #validateAccountType = (walletAccount) => {
        if (!walletAccount.isSoftware) {
            throw new Error('mocking xpubs is currently only supported for software walletAccounts');
        }
    };
    #processAccount = async (walletAccount, account) => {
        this.#validateAccountType(walletAccount);
        for (const [assetName, details] of Object.entries(account)) {
            await this.#processAssetSource(walletAccount, assetName, details);
        }
    };
    #ingestMocks = async (mockXPubs) => {
        await this.#publicKeyStore.clearSoftwareWalletAccountKeys();
        if (!mockXPubs) {
            return;
        }
        const walletAccounts = await this.#walletAccountsAtom.get();
        for (const [walletAccountName, account] of Object.entries(mockXPubs)) {
            const walletAccount = walletAccounts[walletAccountName];
            if (!walletAccount) {
                this.#logger.debug('Account not found, cannot mock XPUBs: ', walletAccountName);
                continue;
            }
            await this.#processAccount(walletAccount, account);
        }
    };
    mockXPub = async (xpub) => {
        this.#validateAccountType(xpub.walletAccount);
        await this.#publicKeyStore.add(xpub);
    };
    unmockXPub = async (params) => {
        return this.#publicKeyStore.delete(params);
    };
    clearAllMocks = async () => {
        await this.#publicKeyStore.clearSoftwareWalletAccountKeys();
    };
}
const factory = (dependencies) => new MockablePublicKeyProvider(dependencies);
const mockablePublicKeyProviderDefinition = {
    id: 'mockablePublicKeyProvider',
    type: 'module',
    factory,
    dependencies: [
        'publicKeyStore',
        'keychain',
        'walletAccountsAtom',
        'assetsModule',
        'logger',
        'config',
        'getBuildMetadata',
    ],
    public: true,
};
export default mockablePublicKeyProviderDefinition;
